import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AnimatePresence, motion } from 'motion/react'
import { Info, X } from 'lucide-react'
import {
  roadIdByPublicId,
  routeRoadIdsByPublicId,
  routeNameByPublicId,
  routeTrimsByPublicId,
  routeUsernameByPublicId,
} from '@/lib/roads/share'
import MapCanvas from '@/components/map/MapCanvas'
import TopBar from '@/components/panels/TopBar'
import FiltersPanel from '@/components/panels/FiltersPanel'
import RoadPanel from '@/components/panels/RoadPanel'
import SavedRoutesPanel from '@/components/panels/SavedRoutesPanel'
import TrimOverlay from '@/components/map/TrimOverlay'
import FindRideModal from '@/components/panels/FindRideModal'
import RouteTray from '@/components/panels/RouteTray'
import SuggestRoad from '@/components/panels/SuggestRoad'
import Onboarding from '@/components/panels/Onboarding'
import WelcomePopup from '@/components/panels/WelcomePopup'
import InfoBar from '@/components/panels/InfoBar'
import { useRoadStore } from '@/lib/roads/store'
import StatsPanel from '@/components/panels/StatsPanel'
import { initAuth } from '@/lib/auth/auth'
import { useDeepLinkOg } from '@/hooks/useDeepLinkOg'

function NavBanner() {
  const ride = useRoadStore((s) => s.ride)
  const roads = useRoadStore((s) => s.roads)
  const exitRide = useRoadStore((s) => s.exitRide)

  const [index, setIndex] = useState(0)
  const [remaining, setRemaining] = useState(0)

  const rideRoads = (ride?.roadIds ?? [])
    .map((id) => roads.find((r) => r.id === id))
    .filter((r): r is NonNullable<typeof r> => !!r)

  const totalMiles = rideRoads.reduce((s, r) => s + (r.distance_miles ?? 15), 0)

  useEffect(() => {
    setIndex(0)
    setRemaining(totalMiles)
    const roadTimer = setInterval(() => setIndex((i) => (rideRoads.length ? (i + 1) % rideRoads.length : 0)), 8000)
    const distTimer = setInterval(() => setRemaining((m) => Math.max(0, m - 0.2)), 1000)
    return () => {
      clearInterval(roadTimer)
      clearInterval(distTimer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ride?.roadIds.join(',')])

  if (!ride || rideRoads.length === 0) return null

  const current = rideRoads[Math.min(index, rideRoads.length - 1)]
  const estHours = (remaining / 42).toFixed(1)

  return (
    <motion.div
      initial={{ opacity: 0, y: -16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      className="absolute top-[136px] md:top-[76px] left-1/2 -translate-x-1/2 z-40 w-[min(92vw,440px)]"
    >
      <div className="rounded-xl bg-[#0C0C0E]/95 backdrop-blur border border-amber-400/40 p-4 space-y-2.5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-amber-400">
              Now riding
            </div>
            <div className="text-base font-bold tracking-tight text-white mt-0.5 truncate max-w-72">
              {current.name}
            </div>
          </div>
          <button
            onClick={exitRide}
            className="h-11 w-11 -mr-2 flex items-center justify-center rounded-lg text-white/50 hover:text-white"
            title="Exit navigation"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex items-center gap-4">
          <span className="text-2xl font-bold text-amber-400 tracking-tight">
            {remaining.toFixed(1)} mi left
          </span>
          <span className="text-xs text-white/50">~{estHours}h remaining</span>
        </div>

        <div className="h-1 rounded-full bg-white/10 overflow-hidden">
          <div
            className="h-full bg-amber-400 rounded-full transition-all duration-1000"
            style={{ width: `${totalMiles ? 100 - (remaining / totalMiles) * 100 : 0}%` }}
          />
        </div>

        <div className="flex items-start gap-1.5 text-[11px] text-white/40 leading-snug">
          <Info size={12} className="mt-0.5 shrink-0" />
          Off route? RoadRatings guides you back — it never swaps your roads for a boring shortcut.
        </div>

        <button
          onClick={exitRide}
          className="min-h-11 w-full rounded-lg border border-white/20 text-white/80 text-sm font-medium hover:border-amber-400/60"
        >
          EXIT NAVIGATION
        </button>
      </div>
    </motion.div>
  )
}

export default function HomePage() {
  const initRoads = useRoadStore((s) => s.initRoads)
  const disposeRoads = useRoadStore((s) => s.disposeRoads)
  const loading = useRoadStore((s) => s.loading)
  const roads = useRoadStore((s) => s.roads)
  const error = useRoadStore((s) => s.error)
  const ride = useRoadStore((s) => s.ride)
  const dismissOverlaysSeq = useRoadStore((s) => s.dismissOverlaysSeq)
  const setRouteSavedBy = useRoadStore((s) => s.setRouteSavedBy)
  const statsUsername = useRoadStore((s) => s.statsUsername)
  const trimmingRoadId = useRoadStore((s) => s.trimmingRoadId)
  const closeStats = useRoadStore((s) => s.closeStats)
  const { publicId } = useParams<{ publicId: string }>()

  // Link-preview metadata for deep links: title + preview image tags.
  useDeepLinkOg(publicId)

  // Shared deep-links (/road/[id], /route/[id]): once the road list is in,
  // resolve the public id and restore the shared selection — a selected road
  // or a loaded route (with its saved trims). Runs once per page load.
  const deepLinkDone = useRef(false)
  useEffect(() => {
    if (!publicId || deepLinkDone.current || roads.length === 0) return
    deepLinkDone.current = true
    const store = useRoadStore.getState()
    const isRoute = window.location.pathname.startsWith('/route/')
    if (isRoute) {
      void routeRoadIdsByPublicId(publicId).then((ids) => {
        if (ids && ids.length > 0) {
          store.setRouteIds(ids)
          store.focusRoute()
          void routeNameByPublicId(publicId).then((name) => {
            if (name) store.setRouteName(name)
          })
          void routeUsernameByPublicId(publicId).then((username) => {
            setRouteSavedBy(username)
          })
          // Restore the trimmed head/tail cuts saved with the shared route —
          // only for roads that are actually part of the restored route.
          void routeTrimsByPublicId(publicId).then((trims) => {
            if (!trims) return
            for (const [trimKey, trim] of Object.entries(trims)) {
              // Keys are per visit: plain id for the first pass, "id#2"…
              // for later ones — match on the base road id.
              if (ids.includes(trimKey.split('#')[0]) && (trim.head || trim.tail)) {
                store.setTrim(trimKey, trim)
              }
            }
          })
        }
      })
    } else {
      void roadIdByPublicId(publicId).then((id) => {
        if (id && store.roads.some((r) => r.id === id)) store.selectRoad(id)
      })
    }
  }, [publicId, roads.length])

  const [filtersOpen, setFiltersOpen] = useState(false)
  const [findRide, setFindRide] = useState(false)

  // Account panel / admin portal opening bumps dismissOverlaysSeq — close
  // this page's floating panels so they don't stack on top of them.
  useEffect(() => {
    if (dismissOverlaysSeq > 0) {
      setFiltersOpen(false)
      setFindRide(false)
    }
  }, [dismissOverlaysSeq])

  // Lifecycle owner of the roads polling: init on mount, dispose on unmount.
  // StrictMode's setup → cleanup → setup re-initializes cleanly — the store's
  // generation guard discards any request still in flight across the boundary.
  // initAuth stays outside cleanup: auth is app-wide, not owned by this page.
  useEffect(() => {
    initRoads()
    initAuth()
    return () => {
      disposeRoads()
    }
  }, [initRoads, disposeRoads])

  return (
    <div className="relative h-[100dvh] w-full overflow-hidden bg-[#0A0A0B] text-white">
      <MapCanvas />

      <TopBar
        onFindRide={() => setFindRide(true)}
        onToggleFilters={() => setFiltersOpen((v) => !v)}
        active={filtersOpen}
      />
      <FiltersPanel open={filtersOpen} onClose={() => setFiltersOpen(false)} />
      <RoadPanel />
      <SavedRoutesPanel />
      <RouteTray />
      {trimmingRoadId && <TrimOverlay />}
      <FindRideModal open={findRide} onClose={() => setFindRide(false)} />
      <Onboarding />
      <WelcomePopup />
      <InfoBar />
      <StatsPanel
        open={statsUsername !== null}
        username={statsUsername}
        onClose={closeStats}
      />

      <AnimatePresence>{ride && <NavBanner key={ride.roadIds.join(',')} />}</AnimatePresence>

      <AnimatePresence>
        {loading && roads.length === 0 && !error && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 px-4 py-2.5 rounded-full bg-black/80 backdrop-blur border border-white/10 text-sm text-white/70"
          >
            Loading curated roads…
          </motion.div>
        )}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 px-4 py-2.5 rounded-full bg-black/80 backdrop-blur border border-red-400/40 text-sm text-red-300"
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
